import moment from "moment-timezone";
import OfficerAction from "@common/redux/actions/OfficerAction";
import CustomerFormAction from "@common/redux/actions/CustomerFormAction";
import ObservationsAction from "@common/redux/actions/ObservationsAction";
import LinkSessionAction from "@common/redux/actions/LinkSessionAction";
import {
  UIDateFormat,
  UITimeFormat,
  ComplianceAPIDateFormat,
} from "@constants/datetime";
import { parseAgcCheckQuestions } from "./AgcCheck/parseAgcCheckQuestions";

export function prepareCustomers(incidentDetails) {
  const customers = [
    incidentDetails.customer ? [incidentDetails.customer] : null,
    incidentDetails.customers,
    [],
  ].find((item) => item !== null && typeof item !== "undefined");

  return customers.map((customer) => prepareCustomer(customer));
}

export function prepareDateTime(date) {
  const parsed = moment(date);

  if (!parsed.isValid()) {
    return [null, null];
  }
  return [parsed.format("YYYY-MM-DD"), parsed.format("HH:mm")];
}

export function prepareCustomer(customer) {
  const { gender, ageRange, height, build, ethnicity, features } = customer;
  const keys = [gender, ageRange, height, build, ethnicity];
  return {
    ...customer,
    name: `${customer.forename ?? ""} ${customer.surname ?? ""}`,
    features: `${keys.map((x) => x?.name).join(", ")}, ${features}`,
  };
}

export function parseCustomerInteractionResponse(record) {
  const {
    customer,
    reason,
    outcome,
    actionTaken,
    reasonNotSeekingHelp,
    reviewDate,
    endDateOfBreak,
  } = record;
  const [reminderDate, reminderTime] = prepareDateTime(reviewDate);
  const [interactionDate, interactionTime] = prepareDateTime(
    record.interactionDate,
  );

  CustomerFormAction.dispatch({ customers: [prepareCustomer(customer)] });

  return {
    ...record,
    interactionDate,
    interactionTime,
    reason: reason?.id,
    isCustomerVulnerable: true,
    reasonNotSeekHelp: reasonNotSeekingHelp,
    isCustomerOk: outcome?.customerOk,
    outcome: outcome?.id,
    actionTaken: actionTaken?.id,
    setReminder: reviewDate !== null,
    reviewDate: moment(endDateOfBreak).format(ComplianceAPIDateFormat),
    reminderDate,
    reminderTime,
  };
}

export function parseObservationResponse(record) {
  const { moods, behaviour, sessionId, customer } = record;
  const [reminderDate, reminderTime] = prepareDateTime(record.reminderDate);
  const [observationDate, observationTime] = prepareDateTime(
    record.observationDate,
  );
  const linkSession = ![0, null].includes(record.sessionId);

  LinkSessionAction.reset().then(() =>
    LinkSessionAction.dispatch({ sessionId }),
  );
  ObservationsAction.dispatch({
    record,
  });
  CustomerFormAction.dispatch({ customers: [prepareCustomer(customer)] });
  return {
    ...record,
    linkSession,
    linkSessionEnabled: linkSession,
    sessionId: record.sessionId,
    reminderDate,
    reminderTime,
    observationDate,
    observationTime,
    observation: behaviour,
    customerMood: moods.map((x) => x.id),
    setReminder: reminderDate !== null,
  };
}

export function parseAgeVerificationResponse(response) {
  const { dateOfBirth } = response;
  const [date, time] = prepareDateTime(response.ageVerificationDate);
  return {
    ...response,
    time,
    date,
    dateOfBirth: moment(dateOfBirth).format(ComplianceAPIDateFormat),
  };
}

export function parseSelfExclusionResponse({ record }) {
  const [date, time] = prepareDateTime(record.selfExclusionDate);
  return {
    ...record,
    date,
    time,
  };
}

export function parseCustomerIncidentResponse(response) {
  const {
    id,
    rowVersion,
    customerIncident: {
      customers,
      customerIncidentType,
      ambulanceDetails,
      ambulanceCalled,
      fireServiceCalled,
      policeCalled,
      anyoneInjured,
      customerIncidentDetails,
      detailsOfInjured,
      capturedByCctv,
      emergencyServicesAttended,
      timeEmergencyServicesCalled,
      timeEmergencyServicesArrived,
      incidentNumber,
      officer,
    },
  } = response;

  const esTypes = [];

  if (ambulanceCalled) {
    esTypes.push("ambulance");
  }

  if (policeCalled) {
    esTypes.push("police");
  }

  if (fireServiceCalled) {
    esTypes.push("fire");
  }

  const [date, time] = prepareDateTime(response.incidentDate);

  OfficerAction.dispatch({ officer });
  CustomerFormAction.dispatch({
    customers: customers?.map((customer) => {
      const preparedCustomer = prepareCustomer(customer);
      return preparedCustomer;
    }),
  });

  return {
    id,
    rowVersion,
    date,
    time,
    officer,
    customerIncidentType: customerIncidentType,
    capturedByCctv,
    injuries: anyoneInjured,
    injuryDetails: detailsOfInjured ?? "",
    description: customerIncidentDetails,
    esCalled: emergencyServicesAttended,
    allowFuture: true,
    timeServicesContacted: formatTime(timeEmergencyServicesCalled),
    timeServicesArrived: formatTime(timeEmergencyServicesArrived),
    ambulanceDetails: ambulanceDetails ?? "",
    incidentNumber: incidentNumber ?? "",
    esTypes,
    customers,
  };
}

export function parseEnforcementVisitResponse(response) {
  const {
    incidentDate,
    enforcementVisit,
    enforcementVisit: { officer },
  } = response;

  OfficerAction.dispatch({ officer });

  const [date, time] = prepareDateTime(incidentDate);
  return {
    ...response,
    ...enforcementVisit,
    date,
    time,
    officerTypeId: enforcementVisit.enforcementVisitType.id,
    visitDetails: enforcementVisit.visitDetails,
  };
}

export function parseGamblingDisputeResponse(response) {
  const {
    record: {
      id,
      rowVersion,
      incidentDate,
      gamblingDispute,
      gamblingDispute: { customer },
    },
  } = response;
  const [date, time] = prepareDateTime(incidentDate);

  const linkSession = ![0, null].includes(gamblingDispute.sessionId);

  CustomerFormAction.dispatch({ customers: [prepareCustomer(customer)] });

  return {
    id,
    rowVersion,
    ...gamblingDispute,
    linkSession,
    linkSessionEnabled: linkSession,
    sessionGuids: gamblingDispute.sessionGuids,
    date,
    time,
    type: gamblingDispute.gamblingDisputeType.id,
    machine: gamblingDispute.machine,
    description: gamblingDispute.gamblingDisputeDetails,
    cctvCatched: gamblingDispute.capturedByCCTV,
  };
}

export function parseSuspiciousActivityResponse(response) {
  const [date, time] = prepareDateTime(response.incidentDate);
  const {
    suspiciousActivity,
    suspiciousActivity: { customers, officer },
    id,
    rowVersion,
  } = response;

  const linkSession = suspiciousActivity?.sessionGuids?.length > 0;

  CustomerFormAction.dispatch({
    customers: customers.map((customer) => {
      const preparedCustomer = prepareCustomer(customer);
      return preparedCustomer;
    }),
  });
  OfficerAction.dispatch({ officer });

  const customerIds = suspiciousActivity.customers.map((x) => x.id);

  return {
    ...suspiciousActivity,
    id,
    rowVersion,
    activityType: suspiciousActivity.suspiciousActivityType.id,
    machine: suspiciousActivity.machine,
    description: suspiciousActivity.suspiciousActivityDetails,
    date,
    time,
    linkSession,
    linkSessionEnabled: linkSession,
    sessionGuids: suspiciousActivity.sessionGuids,
    dateServicesContact: moment(
      suspiciousActivity.policeContactedDateTime,
    ).format(UIDateFormat),
    timeServicesContacted: formatTime(
      suspiciousActivity.policeContactedDateTime,
    ),
    timeServicesArrived: formatTime(suspiciousActivity.policeArrivedDateTime),
    cctvCatched: suspiciousActivity.capturedByCctv,
    suspects: suspiciousActivity.customers,
    customers: suspiciousActivity.customers,
    customerIds,
    allowFuture: true,
  };
}

export function parseAgcCheckResponse(response, questions) {
  const parsedResponse = {
    ...response,
    user: response.compliance?.createdByPerson?.name ?? "",
    scanResult: {
      siteCode: response.siteCode ?? "",
      location: response.type?.name ?? "",
    },
    questions: parseAgcCheckQuestions(response, questions),
  };

  return parsedResponse;
}

const formatTime = (time) => moment(time).format(UITimeFormat);
